import React, { useState, useRef } from 'react';
import MinusIcon from '@mui/icons-material/RemoveCircleOutline';
import { useSnackbar } from 'material-ui-snackbar-provider';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PlusIcon from '@mui/icons-material/AddCircleOutline';
import Resizer from 'react-image-file-resizer';
import AvatarEditor from 'react-avatar-editor';
import Slider from '@mui/material/Slider';
import Box from '@mui/material/Box';
import cx from 'classnames';

import { ContainedButton, OutlinedButton } from '../../../../components/modules/Buttons';
import { userUpdate, getMyUserInfo, deleteUserAvatar } from '../../../../api/usersApi';
import UploadFileIcon from '../../../../assets/icons/upload-icon-file.svg';
import LinearProgress from '../../../../components/modules/LinearProgress';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import RolePlate from '../../../../components/modules/RolePlate';
import { saveUser } from '../../../../store/user/userSlice';
import Avatar from '../../../../components/modules/Avatar';
import CrossIcon from '../../../../assets/icons/cross.svg';
import Modal from '../../../../components/modules/Modal';
import { NOTIFICATION } from '../../../../enums/ENUMS';
import { displayRole } from '../../../../helpers';
import styles from './ProfileModal.module.scss';
import { enqueueSnackbar } from 'notistack';
import { useHotkeys } from 'react-hotkeys-hook';

type TProfileModal = {
  open: boolean;
  switchModal: () => void;
};

const ProfileModal: React.FC<TProfileModal> = ({ open, switchModal }) => {
  const { currentUser } = useAppSelector((state) => state.user);

  const [userNameAndPass, setUserNameAndPass] = useState({
    username: currentUser?.username,
    oldPassword: '',
  });
  const [passwords, setPasswords] = useState({
    password: '',
    passwordRepeat: '',
  });
  const [avatarUrl, setAvatarUrl] = useState(UploadFileIcon);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const editorRef = useRef<AvatarEditor>(null);
  const [scale, setScale] = useState<number>(1);
  const avatarSubmitButtonRef = useRef<HTMLDivElement>(null);
  const avatarDeleteButtonRef = useRef<HTMLDivElement>(null);
  const modalSubmitButtonRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const [click, setClick] = useState(false);
  const [avatarModal, setAvatarModal] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const notification = useSnackbar();
  const [progress, setProgress] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [error, setError] = useState('');
  const [deleteModal, setDeleteModal] = useState(false);

  const onClose = () => {
    switchModal();
    setShowInput(false);
    setAvatarModal(false);
    setDeleteModal(false);
    setIsDisabled(false);
  };
  const userAvatarOpen = () => {
    setAvatarModal(!avatarModal);
  };
  const openPhotoUploader = () => {
    inputRef?.current?.click();
  };
  const onDrop = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
    const reader = new FileReader();
    const file = e.dataTransfer.files[0];
    reader.onloadend = () => {
      const img = new Image();
      img.src = reader.result as string;
      img.onload = () => {
        setAvatarUrl(img.src);
        setIsFileUploaded(true);
      };
    };
    reader.readAsDataURL(file);
  };
  const onDragOver = (e: React.DragEvent<HTMLInputElement>) => {
    e.preventDefault();
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const photofile = event.target?.files![0];

    const reader = new FileReader();

    reader.onloadend = () => {
      // the file's data URL is available in `reader.result`
      const img = new Image();
      img.src = reader.result as string;
      img.onload = () => {
        setAvatarUrl(img.src);
        setIsFileUploaded(true);
      };
    };
    reader.readAsDataURL(photofile);
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (passwords.password) {
      if (passwords.password !== passwords.passwordRepeat) {
        setError('Passwords do not match.');
        return;
      }
      if (passwords.password.length < 8) {
        setError('It must contain at least 8 characters');
        return;
      }
    }

    try {
      setProgress(true);
      setIsDisabled(true);
      const data = {
        username: userNameAndPass.username,
        oldPassword: userNameAndPass.oldPassword,
        password: passwords.password,
        password_repeat: passwords.passwordRepeat,
      };
      console.log(data);
      const filteredData = Object.fromEntries(Object.entries(data).filter((el) => !!el[1]));
      await userUpdate(filteredData);
      enqueueSnackbar(NOTIFICATION.UPDATED, {
        variant: 'success',
        anchorOrigin: { horizontal: 'right', vertical: 'top' },
      });
      setShowInput(false);
      setError('');
      userNameAndPass.oldPassword = '';
      passwords.password = '';
      passwords.passwordRepeat = '';
      setProgress(false);
      onClose();
    } catch (err: any) {
      if (err?.response?.data?.message) {
        enqueueSnackbar(err.response.data.message, {
          variant: 'error',
          anchorOrigin: { horizontal: 'right', vertical: 'top' },
        });
        setError(err.response.data.message);
      }
      setProgress(false);
    }

    const user = await getMyUserInfo();
    dispatch(saveUser(user.data));
  };

  const handleScale = (event: Event, value: number | number[]) => {
    setScale(value as number);
    setClick(true);
  };
  const cancel = () => {
    setIsFileUploaded(false);
  };
  const handleClick = () => {
    setShowInput(true);
  };

  const setAvatar = async () => {
    try {
      setProgress(true);
      setIsDisabled(true);
      if (editorRef.current) {
        const canvas = editorRef.current.getImage();
        const dataUrl = canvas.toDataURL();
        const blob = await fetch(dataUrl).then((res) => res.blob());
        setIsFileUploaded(!isFileUploaded);
        userAvatarOpen();
        const compressedResult = await new Promise<Blob>((resolve) => {
          Resizer.imageFileResizer(
            blob,
            300,
            300,
            'JPEG',
            80,
            0,
            (uri) => {
              const resultBlob = fetch(uri as string).then((res) => res.blob());
              resolve(resultBlob);
            },
            'base64',
          );
        });
        await userUpdate({ file: compressedResult });
        const user = await getMyUserInfo();
        dispatch(saveUser(user.data));
        notification.showMessage(NOTIFICATION.FILE_UPLOADED);
      }
    } catch (err) {
      notification.showMessage(NOTIFICATION.FILE_NOT_UPLOADED);
    } finally {
      setProgress(false);
    }
  };

  const deleteAvatar = async () => {
    try {
      setProgress(true);
      setIsDisabled(true);
      await deleteUserAvatar();
      const user = await getMyUserInfo();
      dispatch(saveUser(user.data));
      notification.showMessage(NOTIFICATION.DELETED);
    } catch (err) {
      notification.showMessage(NOTIFICATION.NOT_DELETED);
    } finally {
      setProgress(false);
      setDeleteModal(false);
    }
  };
  const nameAndPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDisabled(false);
    setUserNameAndPass({
      ...userNameAndPass,
      [e.target.name]: e.target.value,
    });
  };

  const editPasswords = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsDisabled(false);
    if (e.target.name === 'password') {
      setShowInput(true);
    }
    setPasswords({
      ...passwords,
      [e.target.name]: e.target.value,
    });
  };

  const avatarDeleteModal = () => {
    setDeleteModal(!deleteModal);
  };

  const [passwordShown, setPasswordShown] = useState(false);
  const togglePasswordVisiblity = () => {
    setPasswordShown(!passwordShown);
  };

  const [oldPasswordShown, setOldPasswordShown] = useState(false);
  const toggleOldPasswordVisiblity = () => {
    setOldPasswordShown(!oldPasswordShown);
  };

  useHotkeys('Enter', () => {
    if (progress || !open) return;

    if (isFileUploaded) {
      avatarSubmitButtonRef?.current?.click();
      return;
    }

    if (deleteModal) {
      avatarDeleteButtonRef?.current?.click();
      return;
    }

    modalSubmitButtonRef?.current?.click();
  });

  return (
    <Modal open={open} onClose={onClose} loading={progress}>
      {progress && <LinearProgress />}
      <div className={styles.modalBody}>
        <div className={styles.modalHeader}>
          <Avatar name={currentUser?.username} className={styles.userEditAvatar} avatar={currentUser?.avatar_url} />
          <div className={styles.uploadBtn1}>
            <ContainedButton className={styles.contained} onClick={userAvatarOpen}>
              Upload New
            </ContainedButton>
          </div>
          <div className={styles.uploadBtn2}>
            <OutlinedButton kind="default" onClick={avatarDeleteModal}>
              Delete
            </OutlinedButton>
          </div>
        </div>
        <form className={styles.form} onSubmit={onSubmit}>
          <div className={styles.fieldRelative}>
            <label className={styles.label} htmlFor="1">
              Name
            </label>
            <input
              className={styles.field}
              name="username"
              id="1"
              type="name"
              value={userNameAndPass?.username}
              onChange={nameAndPassword}
            />
          </div>

          <div className={styles.fieldRelative} style={{ marginBottom: '0px' }}>
            <label className={styles.label} htmlFor="email">
              Email
            </label>
            <input
              className={styles.field}
              id="email"
              placeholder={currentUser?.email}
              name="email"
              value={currentUser?.email}
              type="email"
              disabled
            />
          </div>

          <div className={styles.relativeWrapper}>
            <div className={styles.fieldRelative}>
              <label className={styles.label} htmlFor="oldPassword">
                Old Password
              </label>
              <input
                className={cx(styles.field, error ? styles.error : '')}
                placeholder="••••••••••••"
                name="oldPassword"
                type={oldPasswordShown ? 'text' : 'password'}
                value={userNameAndPass.oldPassword}
                onChange={nameAndPassword}
              />
            </div>
            {userNameAndPass.oldPassword.length > 0 && <VisibilityIcon className={styles.eye} onClick={toggleOldPasswordVisiblity} />}
          </div>

          <div className={styles.relativeWrapper} style={{ marginTop: '0px' }}>
            <div className={styles.fieldRelative}>
              <label className={styles.label} htmlFor="password">
                New Password
              </label>
              <input
                placeholder="••••••••••••"
                name="password"
                type={passwordShown ? 'text' : 'password'}
                value={passwords.password}
                className={cx(styles.field, error ? styles.error : '')}
                onClick={handleClick}
                onFocus={handleClick}
                onChange={editPasswords}
              />
            </div>
            {passwords.password.length > 0 && <VisibilityIcon className={styles.eye} onClick={togglePasswordVisiblity} />}
          </div>
          {showInput && (
            <div className={styles.relativeWrapper} style={{ marginTop: '0px' }}>
              <div className={styles.fieldRelative}>
                <label className={styles.label} htmlFor="confirmPassword">
                  Confirm Password
                </label>
                <input
                  className={cx(styles.field, error ? styles.error : '')}
                  placeholder="••••••••••••"
                  name="passwordRepeat"
                  type={passwordShown ? 'text' : 'password'}
                  value={passwords.passwordRepeat}
                  onChange={editPasswords}
                />
              </div>
              {error && <p className={styles.red}>{error}</p>}
              {passwords.passwordRepeat.length > 0 && <VisibilityIcon className={styles.eye} onClick={togglePasswordVisiblity} />}
            </div>
          )}

          {currentUser?.role?.title && (
            <div className={styles.fieldRelative} style={{ marginBottom: '0px' }}>
              <label className={styles.label} htmlFor="email">
                Role
              </label>
              <input className={styles.field} value={currentUser?.role.title.replace('Organization', 'Organisation')} type="email" disabled />
              <RolePlate className={styles.rolePlate} userRole={displayRole(currentUser.role.title)} />
            </div>
          )}

          <div className={styles.actionBtns}>
            <ContainedButton type="submit" disabled={isDisabled}>
              <div ref={modalSubmitButtonRef}>Save</div>
            </ContainedButton>
          </div>
        </form>
      </div>

      <Modal open={avatarModal} onClose={onClose} loading={progress}>
        {!isFileUploaded && (
          <div className={styles.modalContainer}>
            <div className={styles.modalTitle}>
              Upload Avatar
              <img src={CrossIcon} alt="cross" onClick={userAvatarOpen} />
            </div>
            <div onClick={openPhotoUploader} onDrop={onDrop} onDragOver={onDragOver} className={styles.photoUpload}>
              <div className={styles.innerUpload}>
                <input
                  max="5242880"
                  hidden
                  type="file"
                  ref={inputRef}
                  onChange={handleFileChange}
                  size={5e10}
                  accept="image/png, image/jpeg"
                />

                <img src={UploadFileIcon} alt="upload content " style={{ marginTop: '65px' }} />

                <div>
                  Upload designs by drag and drop or <span className={styles.text}> click to upload</span>.
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {isFileUploaded && (
        <Modal open={avatarModal} onClose={onClose} loading={progress}>
          <div className={styles.modalContainer}>
            <div className={styles.modalTitle} style={{ marginBottom: '20px' }}>
              Crop Avatar
              <img src={CrossIcon} alt="cross" onClick={switchModal} />
            </div>
            <div>
              <div>
                <AvatarEditor ref={editorRef} image={avatarUrl} width={300} height={300} scale={click ? scale : 1} borderRadius={400} />

                <Box>
                  <div className={styles.slider} style={{ marginTop: '12px' }}>
                    <MinusIcon fontSize="small" />

                    <Slider
                      sx={{
                        '& .MuiSlider-thumb': {
                          color: '#39B3EA',
                        },
                        '& .MuiSlider-track': {
                          color: '#39b3ea',
                        },
                        '& .MuiSlider-rail': {
                          color: '#acc4e4',
                        },
                        '& .MuiSlider-active': {
                          color: 'green',
                        },
                      }}
                      defaultValue={1}
                      min={1}
                      max={20}
                      aria-label="Default"
                      valueLabelDisplay="auto"
                      onChange={handleScale}
                    />
                    <PlusIcon fontSize="small" />
                  </div>
                </Box>
              </div>
              <div className={styles.flex} style={{ width: '100%', marginTop: '24px' }}>
                <OutlinedButton kind="default" onClick={cancel} style={{ width: '170px', borderRadius: 'var(--ui-border-radius)' }}>
                  Cancel
                </OutlinedButton>
                <ContainedButton onClick={setAvatar} style={{ width: '170px', borderRadius: 'var(--ui-border-radius)' }}>
                  <div ref={avatarSubmitButtonRef}>Save avatar</div>
                </ContainedButton>
              </div>
            </div>
          </div>
        </Modal>
      )}
      <Modal open={deleteModal} onClose={onClose} loading={progress}>
        <div className={styles.modalBody}>
          <div className={styles.modalHeader}>
            <div className={styles.modalTitle}>Are you sure u want to delete avatar ?</div>
          </div>
          <div className={styles.uploadBtn1}>
            <ContainedButton className={styles.btnWidth} disabled={currentUser?.avatar_url === null} onClick={deleteAvatar}>
              <div ref={avatarDeleteButtonRef}>Yes</div>
            </ContainedButton>
          </div>
          <div className={styles.uploadBtn2}>
            <OutlinedButton onClick={avatarDeleteModal}>No</OutlinedButton>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export default ProfileModal;
